import { queryOptions } from '@tanstack/react-query';
import type { TurnRow } from '@/features/orders/domain/model/turn';
import { getOrder } from '@/features/orders/presentation/actions/orders/get-order';
import { getOrders } from '@/features/orders/presentation/actions/orders/get-orders';
import { getPaymentMethods } from '@/features/orders/presentation/actions/orders/get-payment-methods';
import { getOrdersStatsByDateParts } from '@/features/orders/presentation/actions/sales/get-orders-stats';
import { getStoreCharts } from '@/features/orders/presentation/actions/sales/get-store-charts';
import { getStoreDashboardSummary } from '@/features/orders/presentation/actions/sales/get-store-dashboard-summary';
import { getActiveTurn } from '@/features/orders/presentation/actions/turns/get-active-turn';
import type { OrdersSearchParams } from '@/features/orders/presentation/utils/sales/search-params';

type OrdersListParams = Parameters<typeof getOrders>[0]['params'];

/** Todo lo que cuelga de `orders` (listado, detalle, métricas) se invalida con `ordersKeys.all`. */
export const ordersKeys = {
  all: ['orders'] as const,
  list: (locationId: string, params: OrdersListParams) => [...ordersKeys.all, 'list', locationId, params] as const,
  detail: (locationId: string, orderId: string | null) => [...ordersKeys.all, 'detail', locationId, orderId] as const,
  stats: (locationId: string, search: unknown) => [...ordersKeys.all, 'stats', locationId, search] as const,
  storeSummary: (locationId: string, search: unknown) =>
    [...ordersKeys.all, 'store-summary', locationId, search] as const,
  storeCharts: (locationId: string, search: unknown) =>
    [...ordersKeys.all, 'store-charts', locationId, search] as const,
};

export const paymentMethodsKeys = {
  all: ['payment-methods'] as const,
  byLocation: (locationId: string) => [...paymentMethodsKeys.all, locationId] as const,
};

export const turnsKeys = {
  all: ['turns'] as const,
  stale: (locationId?: string) => [...turnsKeys.all, 'stale', locationId] as const,
  summary: (locationId?: string, startedAt?: string, endedAt?: string | null) =>
    [...turnsKeys.all, 'summary', locationId, startedAt, endedAt] as const,
  history: (locationId?: string, page?: number, pageSize?: number) =>
    [...turnsKeys.all, 'history', locationId, page, pageSize] as const,
  salesSummary: (locationSlug?: string, startedAt?: string, endedAt?: string) =>
    [...turnsKeys.all, 'sales-summary', locationSlug, startedAt, endedAt] as const,
  refunds: (locationId?: string, turnId?: string | null, startedAt?: string, endedAt?: string | null) =>
    [...turnsKeys.all, 'refunds', locationId, turnId, startedAt, endedAt] as const,
};

export const ordersListQueryOptions = (locationId: string, params: OrdersListParams) =>
  queryOptions({
    queryKey: ordersKeys.list(locationId, params),
    queryFn: () => getOrders({ locationId, params }),
  });

export const orderDetailQueryOptions = (locationId: string, orderId: string | null) =>
  queryOptions({
    queryKey: ordersKeys.detail(locationId, orderId),
    queryFn: () => getOrder({ orderId: orderId ?? '' }),
    enabled: !!orderId,
  });

export const paymentMethodsQueryOptions = (locationId: string) =>
  queryOptions({
    queryKey: paymentMethodsKeys.byLocation(locationId),
    queryFn: () => getPaymentMethods(locationId),
  });

const STALE_TURN_HOURS = 12;

function getStaleTurnOpenSince(turn: TurnRow | null): Date | null {
  if (!turn || turn.id === 'virtual-active-turn') return null;

  const started = new Date(turn.started_at);
  const hoursOpen = (Date.now() - started.getTime()) / (1000 * 60 * 60);

  return hoursOpen > STALE_TURN_HOURS ? started : null;
}

export const staleTurnQueryOptions = (locationId?: string) =>
  queryOptions({
    queryKey: turnsKeys.stale(locationId),
    queryFn: () => getActiveTurn(locationId!),
    enabled: !!locationId,
    select: getStaleTurnOpenSince,
  });

export const ordersStatsQueryOptions = (locationId: string, search: Awaited<OrdersSearchParams>) =>
  queryOptions({
    queryKey: ordersKeys.stats(locationId, search),
    queryFn: () => getOrdersStatsByDateParts({ locationId, params: search }),
  });

export const storeSummaryQueryOptions = (locationId: string, search: Awaited<OrdersSearchParams>) =>
  queryOptions({
    queryKey: ordersKeys.storeSummary(locationId, search),
    queryFn: () =>
      getStoreDashboardSummary(locationId, {
        from: search.from,
        to: search.to,
        range: search.range,
        devices: search.devices,
      }),
  });

export const storeChartsQueryOptions = (locationId: string, search: Awaited<OrdersSearchParams>) =>
  queryOptions({
    queryKey: ordersKeys.storeCharts(locationId, search),
    queryFn: () =>
      getStoreCharts(locationId, {
        from: search.from,
        to: search.to,
        range: search.range,
        devices: search.devices,
      }),
  });
